import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { SafeHtml } from '@angular/platform-browser';
import { combineLatest, Subject, take } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { CarouselComponent } from '../../shared/components/carousel/carousel.component';
import { ProductCardComponent } from '../../shared/components/product-card/product-card.component';
import { ExpansableCardComponent } from '../../shared/components/expansable-card/expansable-card.component';
import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { ErrorDisplayComponent } from '../../shared/components/error-display/error-display.component';
import { BreadcrumbItem } from '../../shared/components/breadcrumb/breadcrumb-item.interface';
import { ProductV2, ProductV2Variation } from '../../core/models/product-v2.model';
import { ProductV2Service } from '../../core/services/product-v2.service';
import { ProductService } from '../../core/services/product.service';
import {
  GetProductsCategories,
  ProductCategory,
} from '../../core/models/product.model';
import { CartFacade } from '../../core/store/cart/cart.facade';
import { NavigationFacade } from '../../core/store/navigation/navigation.facade';
import { AvailableOffersFacade } from '../../core/store/available-offers/available-offers.facade';
import { GtmService } from '../../core/services/gtm.service';
import { sanitizeSlug } from '../../core/utils/utils';
import { campaignCodeInContext } from '../../core/campaign/campaign-cart-reset';
import { campaignVariations } from '../../core/campaign/campaign-contract';
import { CampaignScopeService } from '../../core/campaign/campaign-scope.service';
import { ImageFallbackPipe } from '../../shared/pipes/image-fallback.pipe';
import { Icon } from '../../core/models/icon.model';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { UManagerSlugService, ChannelData } from '../../core/services/umanager-slug.service';
import { UtmPreservationService } from '../../core/services/utm-preservation.service';
import { ChannelConfigService } from '../../core/services/channel-config.service';
import { MindCatalogStore } from '../../core/store/mind/mind-catalog.store';
import { MindProductMapper } from '../../core/store/mind/mind-product.mapper';
import {
  resolveMindCatalogGroup,
  sortMindCatalogItems,
} from '../../core/store/mind/mind-catalog-group.util';
import { applyMindCatalogImages } from '../../core/utils/channel-content.util';
import {
  ProductPageProductLoadService,
  ProductPageProductLoadResult,
} from '../../core/services/product-page-product-load.service';
import { ProductPageMindCartService } from '../../core/services/product-page-mind-cart.service';
import { filterProductsForCardDisplay } from '../../core/utils/product-card-display.util';
import { BlipGoContractService } from '../../core/services/blip-go-contract.service';
import { isBlipGoProduct } from '../../core/constants/blip-go.constants';
import { isRegistryDomainProductUuid } from '../../core/utils/cart-domain-suggestion-rules';

import {
  computeMindHeroAddToCartDisabled,
  mindCatalogHeroPrice as computeMindCatalogHeroPrice,
  mindCatalogHeroPromo as computeMindCatalogHeroPromo,
  mindProductDetailReloadOptions,
  shouldLoadMindProductDetail,
} from './mind-product-page.util';

@Component({
  selector: 'umkt-product-pages',
  standalone: true,
  imports: [
    CommonModule,
    CarouselComponent,
    ProductCardComponent,
    ExpansableCardComponent,
    BreadcrumbComponent,
    LoadingComponent,
    ErrorDisplayComponent,
    ImageFallbackPipe,
    IconComponent,
    I18nPipe,
  ],
  templateUrl: './product-pages.component.html',
  styleUrls: ['./product-pages.component.scss'],
})
export class ProductPagesComponent implements OnInit, OnDestroy {
  breadcrumbItems: BreadcrumbItem[] = [];
  currentProduct: ProductV2 = {} as ProductV2;
  currentCategory = '';
  currentProductUuid = '';
  selectedPlanId = 0;
  relatedProducts: ProductV2[] = [];
  sanitizedDescription: SafeHtml = '';
  cartFacade = inject(CartFacade);
  navigationFacade = inject(NavigationFacade);
  availableOffersFacade = inject(AvailableOffersFacade);

  currentIcon: Icon = { name: 'office-365', type: 'mondrian-icons' };

  isLoading = true;
  private productLoaded = false;
  private relatedProductsLoaded = false;
  private destroy$ = new Subject<void>();

  private channelData: ChannelData | null = null;
  private previousUtmSource: string | null = null;

  private umanagerSlugService = inject(UManagerSlugService);
  private utmPreservationService = inject(UtmPreservationService);
  private channelConfigService = inject(ChannelConfigService);
  private mindStore = inject(MindCatalogStore);
  private location = inject(Location);
  private productPageProductLoad = inject(ProductPageProductLoadService);
  private campaignScope = inject(CampaignScopeService);
  productPageMindCart = inject(ProductPageMindCartService);
  private productLoadGeneration = 0;
  private blipGoContractService = inject(BlipGoContractService);

  error: string | null = null;
  retryCount = 0;
  private pendingMindSlugLoad = false;
  mindHeroAddToCartDisabled = false;

  get relatedCategoryLabel(): string {
    return this.currentProduct?.categories?.[0]?.name || this.currentCategory || '';
  }

  get isCampaignMode(): boolean {
    return !!campaignCodeInContext();
  }

  get isRegistryDomainProduct(): boolean {
    return isRegistryDomainProductUuid(this.currentProduct?.uuid);
  }

  get mindCatalogHeroPrice(): number {
    return computeMindCatalogHeroPrice(this.currentProduct);
  }

  get mindCatalogHeroPromo() {
    return computeMindCatalogHeroPromo(this.currentProduct);
  }

  get mindCatalogHeroOriginalFormatted(): string {
    const original = this.mindCatalogHeroPromo?.originalPrice ?? 0;
    return original.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  get mindCatalogHeroPromoDurationKey(): string {
    const months = this.mindCatalogHeroPromo?.promotionMonths ?? 0;
    return months === 1
      ? 'components.productCard.promoFirstMonth'
      : 'components.productCard.promoFirstMonths';
  }

  get mindCatalogHeroPromoDurationParams(): Record<string, string | number> {
    return { count: this.mindCatalogHeroPromo?.promotionMonths ?? 0 };
  }

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private productV2Service = inject(ProductV2Service);
  private productService = inject(ProductService);
  private gtm = inject(GtmService);
  private i18nService = inject(I18nService);


  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        distinctUntilChanged(
          (prev, curr) =>
            (prev?.['utm_source'] || null) === (curr?.['utm_source'] || null)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.handleUtmChange());

    this.handleUtmChange();

    this.campaignScope.revision$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.currentProductUuid || !this.isCampaignMode) {
        return;
      }
      this.loadProductData();
    });

    this.cartFacade
      .getCartItems()
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.mindHeroAddToCartDisabled = computeMindHeroAddToCartDisabled(
          items,
          this.currentProduct,
          this.currentProductUuid,
          this.location,
          this.pendingMindSlugLoad
        );
      });

    this.i18nService
      .getCurrentLanguage()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.currentProductUuid || !this.currentCategory) {
          return;
        }

        setTimeout(() => {
          this.productLoaded = false;
          this.relatedProductsLoaded = false;
          this.isLoading = true;

          this.loadProductData(
            mindProductDetailReloadOptions(
              this.location,
              this.pendingMindSlugLoad,
              this.currentProduct
            )
          );
          this.loadRelatedProducts();
        });
      });

    combineLatest([
      this.route.params,
      this.navigationFacade.currentProductUuid$,
      this.navigationFacade.currentCategory$,
    ])
      .pipe(take(1))
      .subscribe(([params, productUuid, category]) => {
        if (productUuid && category) {
          this.currentProductUuid = productUuid;
          this.currentCategory = category;

          this.isLoading = true;
          this.productLoaded = false;
          this.relatedProductsLoaded = false;

          this.loadProductData(
            mindProductDetailReloadOptions(
              this.location,
              this.pendingMindSlugLoad,
              this.currentProduct
            )
          );

          this.navigationFacade.clearCurrentProduct();
        } else {
          const productSlug = params['productSlug'];
          this.currentCategory = params['category'];

          this.isLoading = true;
          this.productLoaded = false;
          this.relatedProductsLoaded = false;

          this.findProductBySlug(productSlug);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getCurrentUtmSource(): string | null {
    return (
      this.route.snapshot?.queryParamMap?.get('utm_source') ||
      this.utmPreservationService.preservedUtmParams?.['utm_source'] ||
      null
    );
  }

  private handleUtmChange(): void {
    const utmSource = this.getCurrentUtmSource();

    if (utmSource === this.previousUtmSource) {
      return;
    }

    this.previousUtmSource = utmSource;

    if (!utmSource) {
      this.channelData = null;
      if (this.currentCategory) {
        this.loadRelatedProducts();
      }
      return;
    }

    this.umanagerSlugService
      .checkSlugExists(utmSource)
      .pipe(take(1))
      .subscribe({
        next: resp => {
          if (resp?.exists) {
            this.umanagerSlugService
              .getChannelBySlug(utmSource)
              .pipe(take(1))
              .subscribe({
                next: channelData => {
                  if (channelData?.status && channelData.status !== 'ACTIVE') {
                    this.channelData = null;
                    this.utmPreservationService.disableUtmAndCleanUrl();
                    if (this.currentCategory) {
                      this.loadRelatedProducts();
                    }
                    return;
                  }

                  this.channelData = channelData || null;
                  if (this.currentCategory) {
                    this.loadRelatedProducts();
                  }
                },
                error: err => {
                  console.error('Error loading channel data:', err);
                  this.channelData = null;
                  if (this.currentCategory) {
                    this.loadRelatedProducts();
                  }
                },
              });
          } else {
            this.channelData = null;
            if (this.currentCategory) {
              this.loadRelatedProducts();
            }
          }
        },
        error: err => {
          console.error('Error checking slug existence:', err);
          this.channelData = null;
          if (this.currentCategory) {
            this.loadRelatedProducts();
          }
        },
      });
  }

  private applyChannelOfferFilter(products: ProductV2[]): ProductV2[] {
    if (this.channelData?.offersUuid && this.channelData.offersUuid.length > 0) {
      return (products || []).filter(p =>
        this.channelData!.offersUuid.includes(p.uuid)
      );
    }
    return products || [];
  }

  private checkLoadingComplete(): void {
    if (this.productLoaded && this.relatedProductsLoaded) {
      this.isLoading = false;
    }
  }

  private findProductBySlug(productSlug: string): void {
    this.isLoading = true;
    this.productLoaded = false;
    this.relatedProductsLoaded = false;

    const catalog = this.mindStore.snapshot;
    if (catalog?.groups?.length) {
      for (const group of catalog.groups) {
        const item = group.items.find(
          i => sanitizeSlug(i.displayName ?? '') === productSlug
        );
        if (item?.purchasePayload?.product?.id != null) {
          this.currentProductUuid = String(item.purchasePayload.product.id);
          this.currentCategory = group.title;
          this.pendingMindSlugLoad = true;
          this.loadProductData({ mindDetail: true });
          return;
        }
      }
    }

    this.productService.getCategories().subscribe({
      next: (categoriesResponse: GetProductsCategories) => {
        const categories = categoriesResponse.data || [];
        const originalCategory = categories.find(
          (cat: ProductCategory) =>
            sanitizeSlug(cat.name) === this.currentCategory
        );

        if (originalCategory) {
          this.currentCategory = originalCategory.name;

          this.productV2Service
            .getProductsByCategory(this.currentCategory)
            .subscribe({
              next: response => {
                const allProducts = response.data || [];
                const products = this.applyChannelOfferFilter(allProducts);
                if (products && products.length > 0) {
                  this.availableOffersFacade.mapProductsVariationsToOffers(products);
                }
                const product = products.find(
                  p => sanitizeSlug(p.name) === productSlug
                );

                if (product) {
                  this.currentProductUuid = product.uuid;
                  this.relatedProducts = this.isCampaignMode
                    ? []
                    : filterProductsForCardDisplay(
                        products.filter(p => p.uuid !== this.currentProductUuid),
                        { hideBlipGo: this.blipGoContractService.shouldHideBlipGo() }
                      );
                  this.relatedProductsLoaded = true;

                  this.loadProductData();
                } else {
                  this.error = this.i18nService.instant(
                    'pages.productPage.errors.notFound'
                  );
                  this.isLoading = false;
                }
              },
              error: error => {
                console.error('Error finding product by slug:', error);
                this.error = this.i18nService.instant(
                  'pages.productPage.errors.loadError'
                );
                this.isLoading = false;
              },
            });
        } else {
          this.error = this.i18nService.instant(
            'pages.productPage.errors.categoryNotFound'
          );
          this.isLoading = false;
        }
      },
      error: (error: Error) => {
        console.error('Error loading categories:', error);
        this.error = this.i18nService.instant(
          'pages.productPage.errors.categoriesLoadError'
        );
        this.isLoading = false;
      },
    });
  }

  loadProductData(options?: { mindDetail?: boolean }): void {
    if (
      shouldLoadMindProductDetail(
        options?.mindDetail,
        this.location,
        this.pendingMindSlugLoad
      )
    ) {
      if (this.pendingMindSlugLoad) {
        this.pendingMindSlugLoad = false;
      }
      this.productPageProductLoad
        .loadMindCatalogDetail(this.currentProductUuid, this.currentCategory)
        .subscribe({
          next: result => this.onProductPageLoadSuccess(result, true),
          error: err => this.onMindProductPageLoadError(err),
        });
      return;
    }

    this.productPageProductLoad
      .loadSvaByUuid(this.currentProductUuid, this.currentCategory)
      .subscribe({
        next: result => this.onProductPageLoadSuccess(result, false),
        error: err => this.onSvaProductPageLoadError(err),
      });
  }

  private onProductPageLoadSuccess(
    result: ProductPageProductLoadResult,
    isMind: boolean
  ): void {
    const generation = ++this.productLoadGeneration;
    if (result.resolvedCategory) {
      this.currentCategory = result.resolvedCategory;
    }
    this.currentProduct = result.product;
    this.sanitizedDescription = result.sanitizedDescription;
    this.selectedPlanId = 0;
    this.currentIcon = result.currentIcon;

    if (
      this.blipGoContractService.shouldHideBlipGo() &&
      isBlipGoProduct(this.currentProduct)
    ) {
      const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({});
      this.router.navigate(['/'], { queryParams });
      return;
    }

    const present = () => {
      if (generation !== this.productLoadGeneration) {
        return;
      }

      this.gtm.viewItem(result.gaItem);
      this.productLoaded = true;

      if (isMind) {
        this.cartFacade
          .getCartItems()
          .pipe(take(1))
          .subscribe(items => {
            this.mindHeroAddToCartDisabled = computeMindHeroAddToCartDisabled(
              items,
              this.currentProduct,
              this.currentProductUuid,
              this.location,
              this.pendingMindSlugLoad
            );
          });
      }

      if (!this.relatedProductsLoaded) {
        this.loadRelatedProducts();
      } else {
        this.checkLoadingComplete();
      }
    };

    if (!this.isCampaignMode) {
      present();
      return;
    }

    void this.campaignScope.load().then(scope => {
      if (generation !== this.productLoadGeneration) {
        return;
      }

      const variations = campaignVariations(this.currentProduct, scope?.products ?? null);
      if (variations) {
        this.currentProduct = { ...this.currentProduct, variations };
      }
      present();
    });
  }

  private onMindProductPageLoadError(err: unknown): void {
    console.error('Error loading MIND product detail:', err);
    const msg = err instanceof Error ? err.message : '';
    if (msg === 'MIND_LOCATION') {
      this.error = this.i18nService.instant(
        'pages.productPage.errors.mindLocationRequired'
      );
    } else if (msg === 'MIND_NOT_FOUND') {
      this.error = this.i18nService.instant('pages.productPage.errors.notFound');
    } else {
      this.error = this.i18nService.instant(
        'pages.productPage.errors.dataLoadError'
      );
    }
    this.productLoaded = true;
    this.relatedProductsLoaded = true;
    this.checkLoadingComplete();
  }

  private onSvaProductPageLoadError(err: unknown): void {
    console.error('Error loading product data:', err);
    this.error = this.i18nService.instant(
      'pages.productPage.errors.dataLoadError'
    );
    this.productLoaded = true;
    this.checkLoadingComplete();
  }

  loadRelatedProducts(): void {
    if (this.isCampaignMode) {
      this.relatedProducts = [];
      this.relatedProductsLoaded = true;
      this.checkLoadingComplete();
      return;
    }
    const navMind = !!(this.location.getState() as { mindProductDetail?: boolean })
      ?.mindProductDetail;
    if (navMind || this.currentProduct?.productType === 'MIND') {
      this.mindStore.catalog$.pipe(take(1)).subscribe(catalog => {
        const group = resolveMindCatalogGroup(
          catalog,
          this.currentCategory,
          this.currentProductUuid
        );
        if (!group) {
          this.relatedProducts = [];
          this.relatedProductsLoaded = true;
          this.checkLoadingComplete();
          return;
        }
        const mindKey = group.groupId;
        this.relatedProducts = applyMindCatalogImages(
          sortMindCatalogItems(group.items).map(i =>
            MindProductMapper.mapItemToProduct(i, mindKey, group.title)
          ),
          this.channelConfigService.mindCatalogImageUrls
        ).filter(product => product.uuid !== this.currentProductUuid);
        this.relatedProductsLoaded = true;
        this.checkLoadingComplete();
      });
      return;
    }

    this.productV2Service
      .getProductsByCategory(this.currentCategory)
      .subscribe({
        next: resp => {
          const allProducts = resp.data || [];
          const filtered = this.applyChannelOfferFilter(allProducts);
          if (filtered && filtered.length > 0) {
            this.availableOffersFacade.mapProductsVariationsToOffers(filtered);
          }
          this.relatedProducts = filterProductsForCardDisplay(
            filtered.filter(
              (product: ProductV2) => product.uuid !== this.currentProductUuid
            ),
            { hideBlipGo: this.blipGoContractService.shouldHideBlipGo() }
          );

          this.relatedProductsLoaded = true;
          this.checkLoadingComplete();
        },
        error: error => {
          console.error('Error loading related products:', error);
          this.error = this.i18nService.instant(
            'pages.productPage.errors.relatedLoadError'
          );
          this.relatedProductsLoaded = true;
          this.checkLoadingComplete();
        },
      });
  }

  scrollToPlans(): void {
    const plansSection = document.getElementById('plans-section');
    if (plansSection) {
      const headerHeight = 80;
      const offset = 100;

      const elementPosition = plansSection.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerHeight - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }

  onSelectVariation(variationIndex: number): void {
    this.selectedPlanId = variationIndex;
  }

  onLearnMore(product: ProductV2): void {
    const categorySlug = sanitizeSlug(this.currentCategory);
    const productSlug = sanitizeSlug(product.name);
    const isMind = product.productType === 'MIND';
    this.currentProductUuid = product.uuid;
    this.isLoading = true;
    this.productLoaded = false;
    this.relatedProductsLoaded = false;
    this.error = null;

    this.loadProductData({ mindDetail: isMind });

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);

    this.navigationFacade.setCurrentProduct(product.uuid, this.currentCategory);

    const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({
      ...(this.route.snapshot?.queryParams || {}),
    });

    this.router.navigate([categorySlug, productSlug], {
      replaceUrl: true,
      queryParams,
      state: isMind ? { mindProductDetail: true } : undefined,
    });
  }

  onAddToCartFromCard(
    event: { plan?: ProductV2Variation | null; quantity: number },
    product: ProductV2
  ): void {
    this.cartFacade.addToCart(product, event.quantity, event.plan || undefined);
  }

  onRetry(): void {
    this.retryCount++;
    this.error = null;
    this.isLoading = true;
    this.productLoaded = false;
    this.relatedProductsLoaded = false;

    this.loadProductData(
      mindProductDetailReloadOptions(
        this.location,
        this.pendingMindSlugLoad,
        this.currentProduct
      )
    );
    this.loadRelatedProducts();
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.src = 'https://mkt-image-front.s3.us-east-1.amazonaws.com/not-found.png';
    }
  }

}
